import { useNavigate } from 'react-router';
import { Alert, Header, Link, SpaceBetween } from '@amzn/awsui-components-console';
import { useArbiterClient } from '../../context/ArbiterClientContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { buildRuleUrl } from '../../utils/evaluationHelpers';
import { RULES_REGISTRY_URL, RULE_AUTHORING_URL } from '../../utils/arnUtils';
import { RuleForm, type RuleFormValues } from './RuleForm';

/**
 * The rules reconciler treats HelianthusArbiterRulesRegistry as the source of truth: each deploy overwrites
 * drifted definitions and soft-deletes active rules the bundle does not contain. Drafts are out of its scope,
 * so a console-created rule survives only until it goes active.
 */
const REGISTRY_WARNING = (
  <Alert type="warning" header="A rule created here is not permanent">
    <SpaceBetween size="s">
      <span>
        Every stage deploys its rules from the HelianthusArbiterRulesRegistry package. That deploy overwrites
        any rule whose definition differs from the registry, and soft-deletes active rules the registry does not
        contain. A draft is left alone, so this page is fine for trying something out, but a rule you activate
        without adding it to the registry will be removed on the next deploy.
      </span>
      <SpaceBetween direction="horizontal" size="l">
        <Link href={RULES_REGISTRY_URL} external externalIconAriaLabel="Opens in a new tab">
          Contribute the rule to the registry
        </Link>
        <Link href={RULE_AUTHORING_URL} external externalIconAriaLabel="Opens in a new tab">
          Rule authoring guide
        </Link>
      </SpaceBetween>
    </SpaceBetween>
  </Alert>
);

export function CreateRulePage() {
  useDocumentTitle('Create Rule');
  const navigate = useNavigate();
  const client = useArbiterClient();

  const handleSubmit = async (values: RuleFormValues) => {
    await client.createRule({ ...values, description: values.description || undefined });
    navigate(buildRuleUrl(values.ruleName));
  };

  return (
    <RuleForm
      initialValues={{ severity: 'MEDIUM', ruleStatus: 'draft' }}
      header={<Header variant="h1" description="Define a new rule for evaluation">Create Rule</Header>}
      banner={REGISTRY_WARNING}
      submitLabel="Create rule"
      onSubmit={handleSubmit}
      onCancel={() => navigate('/rules')}
    />
  );
}
